import { useCallback } from 'react';
import { useTranslate } from '@tolgee/react';
import useI18nScene, { IScene } from 'hooks/useI18nScene';

export default function useTipTrigger() {
  const { t } = useTranslate();
  const sceneName = useI18nScene();

  return useCallback(
    (scene: string, condition: string, waitReply?: boolean) => {
      if (
        [
          'REFUND',
          'SENT_MISTAKE',
          'PROBLEM_OF_PRODUCT',
          'RE_REMARK_SKU_REQ',
          'MODIFY_TRADE_REQ',
          'BARGAIN_MSG',
          'CANCEL_ORDER_REQ',
          'ITEM_CHANGE_REQ',
        ].includes(scene)
      ) {
        return t('trigger.请及时跟进', {
          scene: sceneName[scene as keyof IScene],
        });
      }

      if (scene === 'VALUABLE_BUYER') {
        return t('trigger.VALUABLE_BUYER', {
          scene: sceneName[scene as keyof IScene],
        });
      }

      if (scene === 'RETURN_REFUND_RETRIEVE') {
        if (waitReply === true) {
          return t('trigger.RETURN_REFUND_RETRIEVE_1', {
            scene: sceneName[condition as keyof IScene],
          });
        }
        return t('trigger.RETURN_REFUND_RETRIEVE_0', {
          scene: sceneName[condition as keyof IScene],
        });
      }

      if (scene === 'REFUND_RETRIEVE') {
        if (waitReply === true) {
          return t('trigger.REFUND_RETRIEVE_1', {
            scene: sceneName[condition as keyof IScene],
          });
        }
        return t('trigger.REFUND_RETRIEVE_0', {
          scene: sceneName[condition as keyof IScene],
        });
      }

      if (scene === 'CANCEL_ORDER_RETRIEVE') {
        if (waitReply === true) {
          return t('trigger.CANCEL_ORDER_RETRIEVE_1', {
            scene: sceneName[condition as keyof IScene],
          });
        }
        return t('trigger.CANCEL_ORDER_RETRIEVE_0', {
          scene: sceneName[condition as keyof IScene],
        });
      }

      if (scene === 'NEGATIVE_RATE_RETRIEVE') {
        if (waitReply === true) {
          return t('trigger.NEGATIVE_RATE_RETRIEVE_1');
        }
        return t('trigger.NEGATIVE_RATE_RETRIEVE_0');
      }

      if (scene === 'TAX_REMIND') {
        return t('aide.买家需缴税，请及时提醒并跟进');
      }

      if (scene === 'NEGATIVE_RATE_PRONE_BUYER') {
        return t('chat_remind_negative_buyer_tips');
      }

      return '';
    },
    [t, sceneName],
  );
}

export function useTipManualClose() {
  const { t } = useTranslate();
  const sceneName = useI18nScene();

  return useCallback(
    (name: string, solved: boolean, trigger: Conversation.MsgSysRemindTrigger[]) => {
      if (solved) {
        if (trigger.length <= 1) {
          return t('manualClose.solvedOne', {
            name,
          });
        }
        return t('manualClose.solvedMore', {
          name,
          scene: `${sceneName[trigger[0].scene as keyof IScene]}${
            trigger[0].condition ? ` - ${sceneName[trigger[0].condition as keyof IScene]}` : ''
          }`,
        });
      }

      if (trigger.length <= 1) {
        return t('manualClose.noSolvedOne', {
          name,
        });
      }
      return t('manualClose.noSolvedMore', {
        name,
        scene: `${sceneName[trigger[0].scene as keyof IScene]}${
          trigger[0].condition ? ` - ${sceneName[trigger[0].condition as keyof IScene]}` : ''
        }`,
      });
    },
    [t, sceneName],
  );
}

export function useTipAutoClose() {
  const { t } = useTranslate();
  const sceneName = useI18nScene();

  return useCallback(
    (scene: string, timeout: boolean, condition: string, trigCond: string) => {
      if (
        timeout &&
        [
          'REFUND',
          'SENT_MISTAKE',
          'PROBLEM_OF_PRODUCT',
          'RE_REMARK_SKU_REQ',
          'MODIFY_TRADE_REQ',
          'BARGAIN_MSG',
          'CANCEL_ORDER_REQ',
          'ITEM_CHANGE_REQ',
          'VALUABLE_BUYER',
          'TAX_REMIND',
          'NEGATIVE_RATE_PRONE_BUYER',
        ].includes(scene)
      ) {
        return t('timeoutClose.defaultTip', {
          scene: sceneName[scene as keyof IScene],
        });
      }

      if (scene === 'RETURN_REFUND_RETRIEVE') {
        if (timeout) {
          return t('timeoutClose.RETURN_REFUND_RETRIEVE', {
            scene: sceneName[trigCond as keyof IScene],
          });
        }
        if (['CANCELLED', 'CLOSED', 'ACCEPTED_OR_REFUND_PAID'].includes(condition)) {
          return t(`RETURN_REFUND_RETRIEVE.${condition}`, {
            scene: sceneName[trigCond as keyof IScene],
          });
        }
      }

      if (scene === 'REFUND_RETRIEVE') {
        if (timeout) {
          return t('timeoutClose.REFUND_RETRIEVE', {
            scene: sceneName[trigCond as keyof IScene],
          });
        }
        if (['CANCELLED', 'CLOSED', 'ACCEPTED_OR_REFUND_PAID'].includes(condition)) {
          return t(`REFUND_RETRIEVE.${condition}`, {
            scene: sceneName[trigCond as keyof IScene],
          });
        }
      }

      if (scene === 'CANCEL_ORDER_RETRIEVE') {
        if (timeout) {
          return t('timeoutClose.CANCEL_ORDER_RETRIEVE', {
            scene: sceneName[trigCond as keyof IScene],
          });
        }
        if (['NOT_IN_CANCEL_AND_NOT_CANCELLED', 'CANCELLED'].includes(condition)) {
          return t(`CANCEL_ORDER_RETRIEVE.${condition}`, {
            scene: sceneName[trigCond as keyof IScene],
          });
        }
      }

      if (scene === 'NEGATIVE_RATE_RETRIEVE') {
        if (timeout) {
          return t(`timeoutClose.NEGATIVE_RATE_RETRIEVE`);
        }
        return t(`autoClose.NEGATIVE_RATE_RETRIEVE`);
      }

      return '';
    },
    [t, sceneName],
  );
}
